import { WellKnownChannel } from "@ct-software/ct-well-known-channels";

export class AcquisitionFileColumn {
  name: string;
  dimension: string;
  minValue?: number;
  maxValue?: number;
}

export class AcquisitionFile {
  header: string;
  dimensions: string;
  data: string;
  minTime: number;
  maxTime: number;

  columns: AcquisitionFileColumn[];

  parsedData: object[] = [];

  private static separator: string = ',';

  toString(separator: string) {
    let result = this.columns.map(x => x.name).join(separator) + '\n';
    result += this.columns.map(x => x.dimension).join(separator) + '\n';
    for (let line of this.data.split('\n')) {
      const words = line.split(AcquisitionFile.separator);
      result += new Date(words[0]).getTime() / 1000 + separator;
      result += words.slice(1).join(separator) + '\n';
    }

    return result;
  }

  toCsv(): string {
    return this.header + '\n' + this.data;
  }

  static fromString(data: string, sourceSeparator: string, wellKnownChannels: WellKnownChannel[] = []): AcquisitionFile {
    let result = Object.assign(new AcquisitionFile(), {
      header: null,
      data: '',
      minTime: null,
      maxTime: null
    });

    const lines = data.split('\n');

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      let words = line.split(sourceSeparator).map(x => x.trim());

      switch (i) {
        case 0:
          if (wellKnownChannels.length > 0) {
            words = words.map(word => {
              const wellKnownChannel = wellKnownChannels.find(({name}) => name.toLocaleLowerCase() === word.toLocaleLowerCase());
              return wellKnownChannel?.name || word;
            });
          }

          result.header = words.join(AcquisitionFile.separator);
          result.columns = words.map(x => Object.assign(new AcquisitionFileColumn(), { name: x }));
          break;

        case 1:
          result.dimensions = words.join(AcquisitionFile.separator);
          for (let i = 0; i < words.length && i < result.columns.length; i++) {
            result.columns[i].dimension = words[i];
          }

          break;

        default:
          if (words[0].length < 2) {
            continue;
          }

          const time = parseInt(words[0]);
          if (result.minTime === null || result.minTime > time) {
            result.minTime = time;
          }

          if (result.maxTime === null || result.maxTime < time) {
            result.maxTime = time;
          }
          const timestamp = new Date(time * 1000);
          words[0] = timestamp.toISOString();
          result.data += words.join(AcquisitionFile.separator) + '\n';

          var point = { };
          point[result.columns[0].name] = timestamp;
          for (let j = 1; j < result.columns.length; j++) {
            const value = parseFloat(words[j]);
            let column = result.columns[j];

            if (typeof (column.minValue) === 'undefined' || column.minValue > value) {
              column.minValue = value;
            }

            if (typeof (column.maxValue) === 'undefined' || column.maxValue < value) {
              column.maxValue = value;
            }

            point[column.name] = value;
          }

            result.parsedData.push(point);
          }
      }

    return result;
  }

  static merge(csvFiles: AcquisitionFile[]): AcquisitionFile {
    csvFiles = csvFiles.filter(x => x.parsedData.length > 0);
    const allColumns = <AcquisitionFileColumn[]>[].concat(...csvFiles.map(x => x.columns));
    const distinctColumns = allColumns.reduce((result, currentColumn) => {
      let existingColumn = result.filter(x => x.name === currentColumn.name)[0];
      if (!existingColumn) {
        result.push(Object.assign(new AcquisitionFileColumn(), currentColumn));
        return result;
      }

      if (existingColumn.minValue > currentColumn.minValue) {
        existingColumn.minValue = currentColumn.minValue;
      }

      if (existingColumn.maxValue < currentColumn.maxValue) {
        existingColumn.maxValue = currentColumn.maxValue;
      }

      return result;
    }, <AcquisitionFileColumn[]>[]);

    let result = Object.assign(new AcquisitionFile(), {
      columns: distinctColumns,
      data: '',
      parsedData: [],
      minTime: Math.min(...csvFiles.map(x => x.minTime)),
      maxTime: Math.max(...csvFiles.map(x => x.maxTime))
    });

    for (let file of csvFiles) {
      const headers = file.header.split(AcquisitionFile.separator);
      const columnIndexes = distinctColumns.map(x => x.name).map(x => headers.indexOf(x));
      const lines = file.data.split('\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        let values = line.split(AcquisitionFile.separator);
        result.data += columnIndexes.join(AcquisitionFile.separator) + '\n';
      }

      result.parsedData.push(...file.parsedData);
      result.parsedData.push({});
    }

    result.header = distinctColumns.map(x => x.name).join(AcquisitionFile.separator);
    result.dimensions = distinctColumns.map(x => x.dimension).join(AcquisitionFile.separator);
    return result;
  }
}
